import { createContext, useContext, ReactNode } from 'react';
import { usePreferences } from './PreferencesContext';
import { translations, Translations } from '../i18n';
import { SupportedLanguage } from '../lib/expenseTypeHelpers';

type Language = SupportedLanguage;

type TranslationContextType = {
  t: (key: string, fallback?: string) => string;
  language: Language;
  setLanguage: (lang: Language) => Promise<void>;
  // Deprecated but kept for compatibility if used elsewhere as object access (not likely for 't' function context)
  translations: Translations;
  refreshTranslations: () => Promise<void>;
};

const TranslationContext = createContext<TranslationContextType | undefined>(undefined);

export function TranslationProvider({ children }: { children: ReactNode }) {
  const { preferences, updatePreferences } = usePreferences();

  const searchParams = new URL(window.location.href).searchParams;
  const urlLang = searchParams.get('lang') as Language;
  const language = (urlLang || preferences?.language || 'en') as Language;



  const setLanguage = async (lang: Language) => {
    await updatePreferences({ language: lang });
  };

  /**
   * Helper to access nested properties by string path (e.g. "common.save")
   */
  const getNestedValue = (obj: any, path: string): string | undefined => {
    return path.split('.').reduce((prev, curr) => prev ? prev[curr] : undefined, obj);
  };

  const t = (key: string, fallback?: string): string => {
    // 1. Try current language
    const currentLangObj = translations[language];
    let value = getNestedValue(currentLangObj, key);

    // 2. Try English fallback
    if (!value && language !== 'en') {
      const fallbackLangObj = translations['en'];
      value = getNestedValue(fallbackLangObj, key);
    }

    // 3. Return value -> fallback arg -> key
    return value || fallback || key;
  };

  // No-op for compatibility
  const refreshTranslations = async () => { };

  return (
    <TranslationContext.Provider
      value={{
        t,
        language,
        setLanguage,
        translations: translations[language], // Provide raw object if needed
        refreshTranslations,
      }}
    >
      {children}
    </TranslationContext.Provider>
  );
}

export function useTranslation() {
  const context = useContext(TranslationContext);
  if (context === undefined) {
    throw new Error('useTranslation must be used within a TranslationProvider');
  }
  return context;
}
